import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, Subscription, EMPTY } from 'rxjs';
import { catchError, filter, map, shareReplay, tap } from 'rxjs/operators';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { ToastrService } from 'ngx-toastr';
import { ErrorService } from './error.service';
import { TokenService } from './token.service';
import { IHelloMsg } from '../model/hello-msg';
import { environment } from '../../environments/environment';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class WebsocketService implements OnDestroy {
  private wsUrl = environment.wsUrl;
  private ws: WebSocketSubject<any> | null = null;
  private isConnecting = false;
  private connectionStatusSubject = new BehaviorSubject<boolean>(false);
  private userDevicesSubject = new BehaviorSubject<any>(null);
  public readonly isConnected$: Observable<boolean> = this.connectionStatusSubject.asObservable();
  // Socket-open is not enough to send app messages: the server closes the session ("Good
  // bye!") for anything but HELLO until the HELLO handshake authorizes it. This turns true
  // when the server's AUTHORIZED arrives, so a deep-linked page can wait before sending.
  private authorizedSubject = new BehaviorSubject<boolean>(false);
  public readonly authorized$: Observable<boolean> = this.authorizedSubject.asObservable();

  // One stream in, one way out. Every server message is routed by its `type` through
  // on(type); the service does not need to know which types exist, so a new message
  // (CONFIGDIFF, ZIGBEE*) is consumed by a component without touching this file.
  private inboundSubject = new Subject<any>();
  public readonly messages$: Observable<any> = this.inboundSubject.asObservable();

  /** Payloads of one message type. */
  public on<T = any>(type: string): Observable<T> {
    return this.messages$.pipe(
      filter((m: any) => m?.type === type),
      map((m: any) => m.payload as T)
    );
  }

  // Kept as named streams because components already use them. shareReplay(1) matters:
  // these used to be BehaviorSubjects, so a component subscribing AFTER a message still
  // got the current state. Without it, a late subscriber shows stale data until the next
  // event — which looks exactly like "the UI updates a few seconds late".
  public readonly updateIO$: Observable<any> = this.on('UPDATE').pipe(shareReplay(1));
  // Deliberately NOT replayed. A scenario event is a moment, not a state: replaying the last
  // one to a component that mounts later resurrects a finished scenario, which is what made
  // the irrigation card restart a full countdown on every return to the page. Current state
  // for a late subscriber comes from the controller's scenarioState in USERDEVICES.
  public readonly scenarioEvent$: Observable<any> = this.on('SCENARIOEVENT');
  public readonly counterUpdate$: Observable<any> = this.on('COUNTERS').pipe(shareReplay(1));
  private wsSubscription: Subscription | null = null;
  
  private myHello: IHelloMsg = {
    type: 'HELLO',
    payload: {
      token: '',
      type: '',
    },
  };

  constructor(
    private tokenService: TokenService,
    private errorService: ErrorService,
    private toastr: ToastrService,
    private translate: TranslateService
  ) {}

  /**
   * Ensure websocket is connected. Public so components can request a connection.
   */
  public async connect(): Promise<void> {
    if (this.isConnecting) {
      return;
    }
    if (this.ws && !this.ws.closed) {
      // already connected
      return;
    }

    this.isConnecting = true;

    // ensure token is valid before opening socket (best-effort)
    try {
      await this.tokenService.ensureValidAccessToken();
    } catch (e) {
      console.warn('Token ensure failed before WS connect', e);
    }

    // Clean up any previous socket
    if (this.ws) {
      try {
        this.ws.complete();
      } catch {}
      this.ws = null;
    }

    this.ws = webSocket({
      url: this.wsUrl,
      openObserver: {
        next: (ev: Event) => {
          //console.log('WebSocket opened', ev);
          this.isConnecting = false;
          this.connectionStatusSubject.next(true);
          this.sendHelloMessage();
        },
      },
      closeObserver: {
        next: (ev: any) => {
          //console.log('WebSocket closed', ev);
          this.isConnecting = false;
          this.connectionStatusSubject.next(false);
          this.authorizedSubject.next(false);   // a new session must re-authorize
          //this.toastr.error('WebSocket connection closed', 'WebSocket');
          // teardown
          this.teardownAndReconnect();
        },
      },
      // Let errors flow into the observable error channel
    });

    // subscribe to messages and forward to inboundSubject
    try {
      this.wsSubscription = this.ws.pipe(
        tap(msg => {
          if (environment.env == 'dev')
            console.log('WS raw message', msg);
          // USERDEVICES is request/response, not an event stream: getUserDevices() waits
          // on this subject. Everything else just flows out through on(type).
          if (msg.type === 'USERDEVICES')
            this.userDevicesSubject.next(msg.payload);
          if (msg.type === 'AUTHORIZED')
            this.authorizedSubject.next(true);
        }),
        catchError((err) => {
          console.warn('WebSocket stream error', err);
          return EMPTY;
        })
      ).subscribe(msg => this.inboundSubject.next(msg));
    } catch (e) {
      console.error('Failed to subscribe to websocket messages', e);
    }
  }

  private teardownAndReconnect(): void {
    // unsubscribe from messages
    if (this.wsSubscription) {
      try {
        this.wsSubscription.unsubscribe();
      } catch {}
      this.wsSubscription = null;
    }

    // close ws if still present
    if (this.ws) {
      try {
        this.ws.complete();
      } catch {}
      this.ws = null;
    }

    // schedule reconnect
    setTimeout(() => {
      this.connect().catch(err => console.warn('Reconnect failed', err));
    }, 5000);
  }

  public sendMessage(message: any): void {
    if (this.ws && !this.ws.closed) {      
      try {
        this.ws.next(message);
      } catch (e) {
        console.error('Failed to send message over WS', e);
        this.toastr.error(this.translate.instant('ws.send_failed'), 'WebSocket');
      }
    } else {
      //console.error('WebSocket is not connected');
      // this.toastr.error('Unable to send message: WebSocket is not connected', 'WebSocket');
    }
  }

  public getUserDevices(): Observable<any> {  
    this.userDevicesSubject.next(null);      
    this.sendMessage({ type: 'USERDEVICES' });
    return this.userDevicesSubject.asObservable();    
  }
  
  public closeConnection(): void {
    if (this.wsSubscription) {
      try { this.wsSubscription.unsubscribe(); } catch {}
      this.wsSubscription = null;
    }
    if (this.ws) {
      try { this.ws.complete(); } catch {}
      this.ws = null;
    }
    this.isConnecting = false;
    this.connectionStatusSubject.next(false);
  }

  private async sendHelloMessage(): Promise<void> {
    this.myHello.type = 'HELLO';
    this.myHello.payload.type = 'WEB';
    try {
      const token = await this.tokenService.getAccessToken();
      this.myHello.payload.token = token ? String(token) : '';
    } catch (e) {
      console.warn('Failed to get token for HELLO', e);
      this.myHello.payload.token = '';
    }

    //console.log('Sending HELLO message with token');
    try {
      this.sendMessage(this.myHello);
    } catch (e) {
      console.error('Failed to send HELLO over WebSocket', e);
    }
  }

  ngOnDestroy(): void {
    this.closeConnection();
  }
}

